import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { createLead, tags as tagsApi, type LeadPayload } from '../../lib/resources';
import type { Lead, LeadKind } from '../../types';
import { LeadPicker } from '../LeadPicker';
import { Button, ErrorNote, Field, Modal, Tabs, TagPill, inputClass } from '../ui';

type Tab = 'dados' | 'endereco' | 'notas';

const EMPTY_ADDRESS = {
  country: 'BR',
  zip: '',
  address: '',
  number: '',
  complement: '',
  block: '',
  city: '',
  state: '',
};

/**
 * "Novo lead" — criação rápida (doc 02, 4): alterna Pessoa | Empresa, abas Dados,
 * Endereço e Notas, e "Abrir após criar". Campos adicionais e atendente entram depois, na ficha.
 */
export function NewLeadModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: (lead: Lead, open: boolean) => void;
}) {
  const [kind, setKind] = useState<LeadKind>('PERSON');
  const [tab, setTab] = useState<Tab>('dados');
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    tradeName: '',
    phone: '',
    email: '',
    birthDate: '',
    foundationDate: '',
    taxId: '',
    jobTitle: '',
    segment: '',
    source: '',
  });
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [company, setCompany] = useState<{ id: string; name: string } | null>(null);
  const [contact, setContact] = useState<{ id: string; name: string } | null>(null);
  const [parent, setParent] = useState<{ id: string; name: string } | null>(null);
  const [openAfter, setOpenAfter] = useState(true);

  const tagsQuery = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const set = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const text = (value: string) => value.trim() || undefined;

  const mutation = useMutation({
    mutationFn: () => {
      const hasAddress = Object.entries(address).some(
        ([key, value]) => key !== 'country' && value.trim(),
      );
      const payload: LeadPayload = {
        kind,
        name: form.name.trim(),
        phone: text(form.phone),
        email: text(form.email),
        taxId: text(form.taxId),
        source: text(form.source),
        tags: selectedTags.length ? selectedTags : undefined,
        notes: text(notes),
        address: hasAddress
          ? Object.fromEntries(Object.entries(address).filter(([, value]) => value.trim()))
          : undefined,
      };
      if (kind === 'PERSON') {
        Object.assign(payload, {
          displayName: text(form.displayName),
          birthDate: text(form.birthDate),
          jobTitle: text(form.jobTitle),
          companyId: company?.id,
        });
      } else {
        Object.assign(payload, {
          tradeName: text(form.tradeName),
          segment: text(form.segment),
          foundationDate: text(form.foundationDate),
          primaryContactId: contact?.id,
          parentCompanyId: parent?.id,
        });
      }
      return createLead(payload);
    },
    onSuccess: (lead) => {
      onCreated?.(lead, openAfter);
      onClose();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal title="Novo lead" onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs text-slate-500">Criação rápida — complete os detalhes depois.</p>
        <div
          className="inline-flex rounded-lg border border-slate-200 p-0.5"
          role="radiogroup"
          aria-label="Tipo"
        >
          {(
            [
              ['PERSON', 'Pessoa'],
              ['COMPANY', 'Empresa'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              onClick={() => setKind(value)}
              className={`rounded-md px-4 py-1.5 text-sm ${kind === value ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <Tabs
          tabs={[
            ['dados', kind === 'PERSON' ? 'Dados Pessoais' : 'Dados da Empresa'],
            ['endereco', 'Endereço'],
            ['notas', 'Notas'],
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === 'dados' && (
          <div className="grid gap-3 md:grid-cols-2">
            <Field label={kind === 'PERSON' ? 'Nome *' : 'Razão social *'}>
              <input
                required
                autoFocus
                className={inputClass}
                placeholder={
                  kind === 'PERSON' ? 'Informe o nome do lead' : 'Ex: TechCorp Soluções Ltda'
                }
                value={form.name}
                onChange={(event) => set('name', event.target.value)}
              />
            </Field>
            {kind === 'PERSON' ? (
              <Field label="Nome de exibição">
                <input
                  className={inputClass}
                  value={form.displayName}
                  onChange={(event) => set('displayName', event.target.value)}
                />
              </Field>
            ) : (
              <Field label="Nome fantasia">
                <input
                  className={inputClass}
                  placeholder="Ex: TechCorp"
                  value={form.tradeName}
                  onChange={(event) => set('tradeName', event.target.value)}
                />
              </Field>
            )}
            <Field
              label="Telefone"
              hint="Normalizado pelas preferências de telefone da empresa"
            >
              <input
                className={inputClass}
                placeholder="(85) 9 9999-9999"
                value={form.phone}
                onChange={(event) => set('phone', event.target.value)}
              />
            </Field>
            <Field label="E-mail">
              <input
                type="email"
                className={inputClass}
                value={form.email}
                onChange={(event) => set('email', event.target.value)}
              />
            </Field>
            {kind === 'PERSON' ? (
              <>
                <Field label="Empresa associada">
                  <LeadPicker
                    kind="COMPANY"
                    value={company}
                    onChange={setCompany}
                    placeholder="Buscar empresa..."
                  />
                </Field>
                <Field label="Data de nascimento">
                  <input
                    type="date"
                    className={inputClass}
                    value={form.birthDate}
                    onChange={(event) => set('birthDate', event.target.value)}
                  />
                </Field>
                <Field label="Cargo">
                  <input
                    className={inputClass}
                    placeholder="Ex: Gerente de Vendas"
                    value={form.jobTitle}
                    onChange={(event) => set('jobTitle', event.target.value)}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field label="Contato principal">
                  <LeadPicker
                    kind="PERSON"
                    value={contact}
                    onChange={setContact}
                    placeholder="Buscar pessoa..."
                  />
                </Field>
                <Field label="Matriz">
                  <LeadPicker
                    kind="COMPANY"
                    value={parent}
                    onChange={setParent}
                    placeholder="Buscar empresa matriz..."
                  />
                </Field>
                <Field label="Segmento">
                  <input
                    className={inputClass}
                    value={form.segment}
                    onChange={(event) => set('segment', event.target.value)}
                  />
                </Field>
                <Field label="Data de fundação">
                  <input
                    type="date"
                    className={inputClass}
                    value={form.foundationDate}
                    onChange={(event) => set('foundationDate', event.target.value)}
                  />
                </Field>
              </>
            )}
            <Field label="Documento">
              <input
                className={inputClass}
                placeholder="Informe o CPF ou CNPJ"
                value={form.taxId}
                onChange={(event) => set('taxId', event.target.value)}
              />
            </Field>
            <Field label="Origem">
              <input
                className={inputClass}
                placeholder="Como o lead ficou sabendo da sua empresa?"
                value={form.source}
                onChange={(event) => set('source', event.target.value)}
              />
            </Field>
            {(tagsQuery.data?.data ?? []).length > 0 && (
              <div className="md:col-span-2">
                <span className="text-sm font-medium text-slate-700">Tags</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tagsQuery.data?.data.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        setSelectedTags((current) =>
                          current.includes(tag.id)
                            ? current.filter((id) => id !== tag.id)
                            : [...current, tag.id],
                        )
                      }
                    >
                      <span className={selectedTags.includes(tag.id) ? '' : 'opacity-40'}>
                        <TagPill tag={tag} />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'endereco' && (
          <div className="grid gap-3 md:grid-cols-3">
            {(
              [
                ['country', 'País', 2],
                ['zip', 'CEP', 12],
                ['address', 'Endereço', 200],
                ['number', 'Número', 20],
                ['complement', 'Complemento', 120],
                ['block', 'Bairro', 120],
                ['city', 'Cidade', 120],
                ['state', 'UF', 60],
              ] as const
            ).map(([key, label, max]) => (
              <Field key={key} label={label}>
                <input
                  maxLength={max}
                  className={inputClass}
                  value={address[key]}
                  onChange={(event) =>
                    setAddress({
                      ...address,
                      [key]:
                        key === 'country' || key === 'state'
                          ? event.target.value.toUpperCase()
                          : event.target.value,
                    })
                  }
                />
              </Field>
            ))}
          </div>
        )}

        {tab === 'notas' && (
          <Field label="Notas" hint={`${notes.length} / 50.000 caracteres`}>
            <textarea
              rows={6}
              maxLength={50000}
              className={inputClass}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>
        )}

        {mutation.error && (
          <ErrorNote>{errorMessage(mutation.error, 'Não foi possível criar o lead')}</ErrorNote>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={openAfter}
              onChange={(event) => setOpenAfter(event.target.checked)}
            />
            Abrir após criar
          </label>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending || !form.name.trim()}
            >
              {mutation.isPending ? 'Criando…' : 'Criar lead'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
